import Modal from '@components/v2/ui/Modal/Modal';
import { Users } from 'lucide-react';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../../../../utils/testIds/CatalogProducts.testIds';
import {
  CatalogCreationAccessCatalogDates,
  CatalogCreationAccessFieldName,
} from '../../../../../../../creation/catalogCreationAccess.types';
import CatalogAccessEditor from '../../../../../../../creation/components/CatalogAccessEditor';
import { CatalogAccessCountByField } from '../catalogAccessSection.types';
import { useCatalogAccessEditModal } from '../hooks/useCatalogAccessEditModal';
import CatalogAccessChangeDatesModal from './CatalogAccessChangeDatesModal';
import CatalogAccessMassActionsBar from './CatalogAccessMassActionsBar';

const EDIT_MODAL_MAX_VISIBLE_ROWS = 8;

interface CatalogAccessEditModalProps {
  catalogId: string;
  initialField: CatalogCreationAccessFieldName;
  countByField: CatalogAccessCountByField;
  catalogDates: CatalogCreationAccessCatalogDates;
  isReadOnly: boolean;
  onClose: () => void;
}

const CatalogAccessEditModal = ({
  catalogId,
  initialField,
  countByField,
  catalogDates,
  isReadOnly,
  onClose,
}: CatalogAccessEditModalProps) => {
  const {
    tabs,
    rows,
    selectedField,
    selectedRowIds,
    isAllSelected,
    isSelectingAllResults,
    isChangeDatesModalOpen,
    totalSelected,
    totalResults,
    emptyLabel,
    listedEntityIds,
    isApplyingChanges,
    isApplyDisabled,
    clearSelection,
    handleSelectTab,
    handleChangeRowDate,
    handleRemoveRow,
    handleToggleSelectRow,
    handleToggleSelectAll,
    handleToggleSelectAllResults,
    handleRemoveSelected,
    handleChangeSelectedDates,
    handleOpenChangeDatesModal,
    handleCloseChangeDatesModal,
    handleAddEntities,
    handleApply,
    handleEndReached,
  } = useCatalogAccessEditModal({ catalogId, initialField, countByField, onClose });

  const selection = isReadOnly
    ? undefined
    : {
        selectedIds: selectedRowIds,
        isAllSelected,
        isSelectionLocked: isSelectingAllResults,
        onToggleSelectRow: handleToggleSelectRow,
        onToggleSelectAll: handleToggleSelectAll,
      };

  return (
    <>
      <Modal
        isOpen
        size="registration"
        testId={CatalogProductsTestIds.DETAILS_ACCESS_EDIT_MODAL}
        header={{ title: LangEnum.CATALOGS_CREATION_MODAL_SECTION_ACCESS, icon: Users }}
        onClose={onClose}
        slots={{
          headerBottom: totalSelected > 0 && (
            <CatalogAccessMassActionsBar
              selection={{
                totalSelected,
                totalResults,
                isAllSelected,
                isSelectingAllResults,
                onToggleSelectAll: handleToggleSelectAll,
                onToggleSelectAllResults: handleToggleSelectAllResults,
                onDeselectAll: clearSelection,
              }}
              actions={{
                onChangeDates: handleOpenChangeDatesModal,
                onRemoveSelected: handleRemoveSelected,
              }}
            />
          ),
        }}
        footer={{
          primaryAction: isReadOnly
            ? undefined
            : {
                label: LangEnum.GLOBAL_APPLY,
                isLoading: isApplyingChanges,
                isDisabled: isApplyDisabled,
                testId: CatalogProductsTestIds.DETAILS_ACCESS_APPLY,
                onClick: handleApply,
              },
          secondaryAction: {
            label: isReadOnly ? LangEnum.GLOBAL_CLOSE : LangEnum.GLOBAL_CANCEL_LABEL,
            testId: CatalogProductsTestIds.DETAILS_ACCESS_CANCEL,
            onClick: onClose,
          },
        }}
      >
        <CatalogAccessEditor
          tabs={tabs}
          rows={rows}
          selectedField={selectedField}
          emptyLabel={emptyLabel}
          catalogDates={catalogDates}
          listedEntityIds={listedEntityIds}
          maxVisibleRows={EDIT_MODAL_MAX_VISIBLE_ROWS}
          isReadOnly={isReadOnly}
          catalogId={catalogId}
          selection={selection}
          onEndReached={handleEndReached}
          onSelectTab={handleSelectTab}
          onChangeRowDate={handleChangeRowDate}
          onRemoveRow={handleRemoveRow}
          onAddEntities={handleAddEntities}
        />
      </Modal>
      {isChangeDatesModalOpen && (
        <CatalogAccessChangeDatesModal
          catalogDates={catalogDates}
          onClose={handleCloseChangeDatesModal}
          onConfirm={handleChangeSelectedDates}
        />
      )}
    </>
  );
};

export default CatalogAccessEditModal;
